@if (company && !isHidden) {
  <div hlmCard id="setup-checklist">
    <div hlmCardHeader>
      <h2 hlmCardTitle>{{ headline }}</h2>
      <p hlmCardDescription>{{ subline }}</p>
      <div hlmCardAction class="flex items-center gap-1">
        <span hlmBadge [variant]="requiredPhase ? 'default' : 'secondary'">
          {{ currentPercent }}%
        </span>
        <!-- Only once selling is unblocked — a required task is not optional reading -->
        @if (canHide) {
          <button
            hlmBtn
            variant="ghost"
            size="icon-sm"
            aria-label="Übersicht ausblenden"
            title="Übersicht ausblenden"
            (click)="hideOverview()">
            <ng-icon name="lucideX" />
          </button>
        }
      </div>
    </div>

    <div hlmCardContent class="space-y-4">
      <div hlmProgress [value]="currentPercent">
        <div hlmProgressIndicator></div>
      </div>

      <!-- Required tasks are done — shown once, then dismissible for good -->
      <!-- A member has no required phase, and it doesn't sell on its own -->
      @if (!requiredPhase && !congratsBannerDismissed && !company.is_member) {
        <div hlmAlert>
          <ng-icon name="lucidePartyPopper" />
          <p hlmAlertTitle>Glückwunsch – du kannst jetzt Gutscheine verkaufen!</p>
          <p hlmAlertDescription>
            Mit den optionalen Schritten holst du noch mehr aus deinem Shop heraus.
          </p>
          <div hlmAlertAction>
            <button
              hlmBtn
              variant="ghost"
              size="icon-sm"
              aria-label="Schließen"
              (click)="dismissCongratsBanner()">
              <ng-icon name="lucideX" />
            </button>
          </div>
        </div>
      }

      @if (!requiredPhase && allDone) {
        <div hlmAlert variant="info">
          <ng-icon name="lucideCircleCheck" />
          <p hlmAlertTitle>Alles erledigt!</p>
          <p hlmAlertDescription>
            Dein Gutschein-System ist vollständig eingerichtet. Du kannst diese Übersicht ausblenden
            und sie jederzeit über die Glocke oben wieder aufrufen.
          </p>
          <button hlmBtn variant="outline" size="sm" class="mt-2" (click)="hideOverview()">
            <ng-icon name="lucideX" />
            Übersicht ausblenden
          </button>
        </div>
      }

      <!-- ── Open tasks ── -->
      @if (currentTasks.length > 0) {
        <div hlmItemGroup class="gap-2">
          @for (task of currentTasks; track task.id) {
            <div hlmItem variant="outline" class="items-start gap-y-2">
              <div hlmItemContent>
                <a
                  hlmItemTitle
                  class="hover:underline"
                  [routerLink]="task.routerLink"
                  [queryParams]="task.queryParams"
                  [fragment]="task.fragment">
                  {{ task.title }}
                </a>
                <span hlmItemDescription>{{ task.description }}</span>
              </div>

              <!-- basis-full wraps the actions onto their own row below the text -->
              <div hlmItemActions class="basis-full flex-col items-end justify-end gap-1">
                <div class="flex items-center gap-1">
                  @if (task.allow_manual_confirmation) {
                    <button
                      hlmBtn
                      variant="ghost"
                      size="sm"
                      [disabled]="dismissingTaskId === task.id"
                      (click)="markDone(task.id)">
                      <ng-icon name="lucideCheck" />
                      Erledigt
                    </button>
                  }
                  <a
                    hlmBtn
                    size="sm"
                    [routerLink]="task.routerLink"
                    [queryParams]="task.queryParams"
                    [fragment]="task.fragment">
                    Einrichten
                    <ng-icon name="lucideArrowRight" data-icon="inline-end" />
                  </a>
                </div>

                @if (!task.required) {
                  <button
                    hlmBtn
                    variant="link"
                    size="xs"
                    class="text-muted-foreground px-0"
                    [disabled]="dismissingTaskId === task.id"
                    (click)="dismissTask(task.id)">
                    {{ dismissingTaskId === task.id ? '…' : 'Nicht nötig' }}
                  </button>
                }
              </div>
            </div>
          }
        </div>
      }

      <!-- ── Completed ── -->
      @if (currentCompletedTasks.length > 0) {
        <ul class="space-y-1.5">
          @for (task of currentCompletedTasks; track task.id) {
            <li class="text-muted-foreground flex items-center gap-2 text-sm">
              <ng-icon name="lucideCircleCheck" class="shrink-0" />
              <a
                class="line-through hover:no-underline"
                [routerLink]="task.routerLink"
                [queryParams]="task.queryParams"
                [fragment]="task.fragment">
                {{ task.title }}
              </a>
            </li>
          }
        </ul>
      }

      <!-- ── Skipped ── -->
      @if (dismissedTasks.length > 0) {
        <div>
          <button
            hlmBtn
            variant="ghost"
            size="xs"
            class="text-muted-foreground px-0"
            (click)="toggleSkipped()">
            <ng-icon
              name="lucideChevronRight"
              class="transition-transform"
              [class.rotate-90]="skippedSectionExpanded" />
            {{ dismissedTasks.length }} übersprungene
            {{ dismissedTasks.length === 1 ? 'Aufgabe' : 'Aufgaben' }}
          </button>

          @if (skippedSectionExpanded) {
            <ul class="mt-2 space-y-1.5">
              @for (task of dismissedTasks; track task.id) {
                <li class="text-muted-foreground flex items-center gap-2 text-sm">
                  <ng-icon name="lucideX" class="shrink-0" />
                  <a
                    class="hover:underline"
                    [routerLink]="task.routerLink"
                    [queryParams]="task.queryParams"
                    [fragment]="task.fragment">
                    {{ task.title }}
                  </a>
                </li>
              }
            </ul>
          }
        </div>
      }
    </div>
  </div>
}
